import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { UserOnboardingService } from '../../../public/user-onboarding/user-onboarding.service';
import { SharedService } from '../../../shared/shared.service';

@Component({
  selector: 'mmb-web-app-add-spoken-languages-modal',
  templateUrl: './add-spoken-languages-modal.component.html',
  styleUrls: ['./add-spoken-languages-modal.component.scss']
})
export class AddSpokenLanguagesModalComponent implements OnInit {
  @Output() addSuccess: EventEmitter<string> = new EventEmitter<string>();
  @Output() closed: EventEmitter<string> = new EventEmitter<string>();

  // @Input() language: string;
  @Input() isEditMode: boolean;
  @Input() selectedLanguages: any[] = [];
  @Input() proficiencyLevels: any[];

  languageOptions: any;
  focusText: boolean;

  languageOptions$ = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map(term =>
        this.languageOptions.filter(v => v.name.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, 10)
      )
    );

  constructor(public modal: NgbActiveModal, private sharedService: SharedService,
    private userOnboardingService: UserOnboardingService,
    private toastrService: ToastrService) { }

  ngOnInit() {
    this.sharedService.referenceTypes$.subscribe((value) => {
      if (value) {
        this.languageOptions = value.type_reference_language;
      }
    });
  }

  addLanguage() {
    this.addSuccess.emit();
  }

  /**
   * Focus event for autocomplete
   * @param e 
   */
  public onFocus(e: Event): void {
    this.focusText = true;
    e.stopPropagation();

    setTimeout(() => {
      const inputEvent: Event = new Event('input');
      e.target.dispatchEvent(inputEvent);
    }, 0);
  }

  onClose() {
    this.closed.emit();
  }

  public onBlurText() {
    this.focusText = false;
  }

  /**
   * Used to format the result data from the lookup into the
   * display and list values. Maps `{name: "country", id:"id" }` into a string
   */
  resultFormat(value: any) {
    if (value.name_ascii) {
      return value.name_ascii;
    }
    return value.name;
  }


  /**
   * Initially binds the string value and then after selecting
   * an item by checking either for string or key/value object.
   */
  inputFormat(value: any) {
    if (value.name_ascii) {
      return value.name_ascii;
    }
    if (value.name) {
      return value.name;
    }
    return value;
  }

  onLanguageSelected(ev: any, input) {
    ev.preventDefault();
    let foundItem = this._findItemByLanguage(ev.item);
    if (foundItem) {
      this.toastrService.error('Language is already added to the list', 'Error');
      return;
    }
    this.selectedLanguages.push({
      language: ev.item,
      level: {}
    });
    input.value = '';
  }

  onSelectLevel(language, level) {
    // check if same level is selected, then delete it
    let foundItem = this._findItemByLanguage(language);

    if (foundItem && foundItem.level.value === level.value) {
      this._deleteLanguageLevel(foundItem, language, level);
      return;
    } else if (foundItem && foundItem.level && foundItem.level.value) {
      this._deleteLanguageLevel(foundItem, language, level, true);
      return;
    }

    this._saveLanguageLevel(language, level);
  }

  isLanguageLevelSelected(language, level) {
    return this.selectedLanguages.find((item) => {
      return item.language.value === language.value && item.level.value === level.value;
    })
  }

  private _findItemByLanguage(language: any) {
    return this.selectedLanguages.find((item) => {
      return item.language.value === language.value;
    });
  }

  private _findItemIndexByLanguage(language: any) {
    return this.selectedLanguages.findIndex((item) => {
      return item.language.value === language.value;
    });
  }

  private _deleteLanguageLevel(savedLanguageLevel, language, level, isSaveNewLevel?: boolean) {
    this.userOnboardingService.deleteUserLanguageLevel(savedLanguageLevel.uuid).subscribe((response) => {
      // savedLanguageLevel.level = {};
      let itemIndex = this._findItemIndexByLanguage(language);
      this.selectedLanguages.splice(itemIndex, 1);
      if (isSaveNewLevel) {
        this._saveLanguageLevel(language, level);
      }
    })
  }

  private _saveLanguageLevel(language: any, level: any) {
    this.userOnboardingService.saveUserLanguageLevel(language.value, level.value).subscribe((response: any) => {
      let foundItem = this._findItemByLanguage(language);
      if (foundItem) {
        foundItem.level = level;
        foundItem.uuid = response.uuid;
      }
    })
  }
}
